/*
 * Cart milestone tiers (Theme settings > Cart).
 *
 * - "free_product" tiers: the tier product is auto-added (qty 1, `_tier_gift` property)
 *   once the tier is reached and removed when the cart drops below it.
 * - "mystery_box" tiers: the customer picks one product from the tier collection in the
 *   cart drawer popup; it's added with the `_mystery_box` property and removed if the
 *   tier is no longer reached.
 *
 * Prices are enforced by Shopify automatic discounts; this script only keeps the cart
 * contents in line with the tiers. Config comes from snippets/cart-tiers-config.liquid.
 */
(() => {
  const config = window.cartTiers;
  if (!config || !config.enabled || !config.tiers.length) return;

  const nativeFetch = window.fetch.bind(window);
  const CART_MUTATION_URL = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  const failedVariants = new Set();
  let syncTimer = null;
  let syncing = false;
  let syncAgain = false;
  let refreshAgain = false;

  const isGift = (item) => item.properties && (item.properties._tier_gift || item.properties._freebie);
  const isMystery = (item) => item.properties && item.properties._mystery_box;
  // Lines of a free product tier: its own tagged gift, plus untagged copies of its product
  // (direct cart links, checkout apps) so the extras can be removed. A line tagged for another
  // tier belongs to that tier, so two tiers can give away the same product.
  const isGiftProduct = (item, tier) => {
    if (tier.reward !== 'free_product') return false;
    const giftTier = item.properties?._tier_gift;
    if (giftTier) return giftTier === tier.id;
    return tier.productId ? item.product_id === tier.productId : item.variant_id === tier.variantId;
  };
  const isAnyGiftProduct = (item) => config.tiers.some((tier) => isGiftProduct(item, tier));

  function getProgress(cart) {
    let subtotal = 0;
    let count = 0;
    cart.items.forEach((item) => {
      if (isGift(item) || isAnyGiftProduct(item)) return;
      subtotal += item.final_line_price;
      if (!isMystery(item)) count += item.quantity;
    });
    return { amount: subtotal / 100, quantity: count };
  }

  const isReached = (tier, progress) =>
    (tier.type === 'quantity' ? progress.quantity : progress.amount) >= tier.threshold;

  async function postJSON(url, body) {
    const response = await nativeFetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    if (!response.ok) throw new Error(`${url} failed with ${response.status}`);
    return response.json();
  }

  const getCart = () => nativeFetch(`${window.routes?.cart_url || '/cart'}.js`).then((r) => r.json());

  // Works out which lines to change/add so the cart matches the reached tiers.
  function planChanges(cart) {
    const progress = getProgress(cart);
    const updates = {};
    const adds = [];

    config.tiers.forEach((tier) => {
      const reached = isReached(tier, progress);

      if (tier.reward === 'free_product') {
        // Only one unit of the free product is allowed, and only on the tagged gift line:
        // every other line of it (tagged or not) is removed.
        const lines = cart.items.filter((item) => isGiftProduct(item, tier));
        const wanted = reached && tier.variantId && !failedVariants.has(tier.variantId);
        const keep = wanted
          ? lines.find((item) => item.properties?._tier_gift === tier.id && item.variant_id === tier.variantId)
          : null;

        lines.forEach((item) => {
          if (item !== keep) updates[item.key] = 0;
        });
        if (keep && keep.quantity !== 1) updates[keep.key] = 1;
        if (wanted && !keep) {
          adds.push({ id: tier.variantId, quantity: 1, properties: { _tier_gift: tier.id } });
        }
      }

      if (tier.reward === 'mystery_box') {
        const lines = cart.items.filter((item) => item.properties?._mystery_box === tier.id);
        // Customers may buy more than one of the picked product; the discount limits the 50% off
        // to one unit, and Shopify then splits the pick into a discounted and a full-price line.
        // Keep every line of the picked product; drop the pick when locked, or other products'
        // picks left behind.
        const pickedVariant = lines[0]?.variant_id;
        lines.forEach((item) => {
          if (!reached || item.variant_id !== pickedVariant) updates[item.key] = 0;
        });
      }
    });

    // Gifts from tiers that no longer exist, and the old single-freebie solution.
    const tierIds = config.tiers.map((tier) => tier.id);
    cart.items.forEach((item) => {
      const giftTier = item.properties?._tier_gift;
      const mysteryTier = item.properties?._mystery_box;
      if (item.properties?._freebie) updates[item.key] = 0;
      if (giftTier && !tierIds.includes(giftTier)) updates[item.key] = 0;
      if (mysteryTier && !tierIds.includes(mysteryTier)) updates[item.key] = 0;
    });

    return { updates, adds };
  }

  async function sync({ refresh = false } = {}) {
    if (syncing) {
      syncAgain = true;
      refreshAgain = refreshAgain || refresh;
      return;
    }
    syncing = true;
    toggleCheckout(true);

    try {
      const { updates, adds } = planChanges(await getCart());
      let changed = false;

      if (Object.keys(updates).length) {
        await postJSON(`${window.routes?.cart_update_url || '/cart/update'}.js`, { updates });
        changed = true;
      }
      for (const item of adds) {
        try {
          await postJSON(`${window.routes?.cart_add_url || '/cart/add'}.js`, { items: [item] });
          changed = true;
        } catch (error) {
          // Sold out / unavailable gift: don't retry it on every cart change.
          failedVariants.add(item.id);
          console.error('Cart tiers: could not add free product', error);
        }
      }

      if (changed || refresh) await refreshCartUI();
    } catch (error) {
      console.error('Cart tiers: sync failed', error);
    } finally {
      syncing = false;
      toggleCheckout(false);
      if (syncAgain) {
        const pendingRefresh = refreshAgain;
        syncAgain = false;
        refreshAgain = false;
        sync({ refresh: pendingRefresh });
      }
    }
  }

  function scheduleSync() {
    clearTimeout(syncTimer);
    syncTimer = setTimeout(sync, 250);
  }

  function toggleCheckout(disabled) {
    document.querySelectorAll('.cart__checkout-button').forEach((button) => {
      button.classList.toggle('is-disabled', disabled);
    });
  }

  // Re-renders the cart drawer and cart icon without opening the drawer.
  async function refreshCartUI() {
    if (window.location.pathname === (window.routes?.cart_url || '/cart')) {
      window.location.reload();
      return;
    }

    const sections = await nativeFetch(`${window.Shopify?.routes?.root || '/'}?sections=cart-drawer,cart-icon-bubble`).then(
      (r) => r.json()
    );
    const parse = (html, selector) => new DOMParser().parseFromString(html, 'text/html').querySelector(selector);

    const drawer = document.querySelector('cart-drawer');
    const drawerContent = document.getElementById('CartDrawer');
    const newDrawerContent = sections['cart-drawer'] && parse(sections['cart-drawer'], '#CartDrawer');
    if (drawer && drawerContent && newDrawerContent) {
      drawerContent.innerHTML = newDrawerContent.innerHTML;
      const isEmpty = !!newDrawerContent.querySelector('.drawer__inner-empty');
      drawer.classList.toggle('is-empty', isEmpty);
      drawer.querySelector('#CartDrawer-Overlay')?.addEventListener('click', drawer.close.bind(drawer));
    }

    const bubble = document.getElementById('cart-icon-bubble');
    const newBubble = sections['cart-icon-bubble'] && parse(sections['cart-icon-bubble'], '.shopify-section');
    if (bubble && newBubble) bubble.innerHTML = newBubble.innerHTML;

    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-tiers', cartData: await getCart() });
    }
  }

  async function selectMysteryItem(tierId, variantId) {
    const cart = await getCart();
    const tier = config.tiers.find((t) => t.id === tierId);
    if (!tier || !isReached(tier, getProgress(cart))) throw new Error('Mystery box is locked');

    const updates = {};
    cart.items.forEach((item) => {
      if (item.properties?._mystery_box === tierId) updates[item.key] = 0;
    });
    if (Object.keys(updates).length) {
      await postJSON(`${window.routes?.cart_update_url || '/cart/update'}.js`, { updates });
    }
    await postJSON(`${window.routes?.cart_add_url || '/cart/add'}.js`, {
      items: [{ id: Number(variantId), quantity: 1, properties: { _mystery_box: tierId } }],
    });
    // The mystery item adds cart value, which can unlock a free product tier.
    await sync({ refresh: true });
  }

  // Theme scripts (product form, cart drawer, BYOB, mm-ajaxcart) all change the cart in
  // their own way, so watch the requests instead of relying on each one publishing events.
  window.fetch = function (input, init) {
    const url = typeof input === 'string' ? input : input?.url || '';
    const request = nativeFetch(input, init);
    if (CART_MUTATION_URL.test(url)) request.then(scheduleSync, () => {});
    return request;
  };

  const nativeOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url, ...rest) {
    if (CART_MUTATION_URL.test(String(url))) this.addEventListener('loadend', scheduleSync);
    return nativeOpen.call(this, method, url, ...rest);
  };

  document.addEventListener('DOMContentLoaded', () => {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event?.source !== 'cart-tiers') scheduleSync();
      });
    }
    sync();
  });

  class CartMysteryBox extends HTMLElement {
    connectedCallback() {
      this.modal = this.querySelector('[data-mystery-modal]');
      this.addEventListener('click', this.onClick.bind(this));
      this.addEventListener('keyup', (event) => {
        if (event.code === 'Escape' && this.modal && !this.modal.hidden) {
          event.stopPropagation();
          this.close();
        }
      });
    }

    onClick(event) {
      if (event.target.closest('[data-mystery-open]')) this.open();
      else if (event.target.closest('[data-mystery-close]')) this.close();
      else {
        const button = event.target.closest('[data-mystery-select]');
        if (button) this.select(button);
      }
    }

    open() {
      if (!this.modal) return;
      this.modal.hidden = false;
      this.anchorBelowGoals();
      this.modal.querySelector('[role="dialog"]').focus();
    }

    // Start the sheet just under the milestone bar so the tiers stay visible. Falls back to a
    // regular bottom sheet when the bar is scrolled away or would leave too little room.
    anchorBelowGoals() {
      this.modal.style.removeProperty('top');
      this.modal.classList.remove('is-anchored');
      const goals = document.querySelector('cart-drawer .cart-goals');
      if (!goals) return;

      const area = this.modal.getBoundingClientRect();
      const offset = goals.getBoundingClientRect().bottom - area.top + 8;
      if (offset <= 0 || offset > area.height * 0.45) return;

      this.modal.style.top = `${offset}px`;
      this.modal.classList.add('is-anchored');
    }

    close() {
      if (!this.modal) return;
      this.modal.hidden = true;
      this.querySelector('[data-mystery-open]')?.focus();
    }

    async select(button) {
      if (button.classList.contains('is-selected')) return this.close();

      const variantId = button.closest('[data-mystery-item]').querySelector('[data-mystery-variant]').value;
      const error = this.querySelector('[data-mystery-error]');
      this.querySelectorAll('[data-mystery-select]').forEach((b) => (b.disabled = true));
      button.classList.add('loading');
      error.hidden = true;

      try {
        await selectMysteryItem(this.dataset.tier, variantId);
      } catch (e) {
        console.error('Cart tiers: could not add mystery box item', e);
        error.textContent = window.cartStrings?.error || 'Something went wrong. Please try again.';
        error.hidden = false;
        this.querySelectorAll('[data-mystery-select]').forEach((b) => (b.disabled = false));
        button.classList.remove('loading');
      }
    }
  }

  if (!customElements.get('cart-mystery-box')) customElements.define('cart-mystery-box', CartMysteryBox);
})();
